import {
  DEPOSIT_DETAIL_MAX_LENGTH,
  DEPOSIT_ORIGINS,
  type DepositOrigin,
  isDepositOrigin,
  namesAForbiddenDepositWay,
  originNeedsALine,
} from "@hakili/domain-types";
import { t } from "@hakili/i18n";
import type { ReactElement } from "react";

/**
 * `OD-37`, round 7 (the agent's R4, data half), workflow v3 Step 0 (financing request) and section 3.6, the
 * founder's apport rulings of 8 October, the architect's of 10 October: **where the buyer's deposit is , declared
 * at the step where the file is opened, from a closed list, nothing pre-selected.**
 *
 * The screen shows the choice only when there is a deposit. Cash, mobile money and money paid to the dealership are
 * not offered, and a free line that names one is refused ON THE SPOT in the founder's sentence , the door refuses
 * the same words (`@hakili/domain-types` holds the one rule). Whether the deposit sits on the account his salary is
 * paid into is the buyer's own answer: « non » announces the account statement he will be asked for, which the
 * file then lists as a piece. The screen decides nothing else.
 */
export interface ApportDeclaration {
  readonly origin: string | null;
  readonly detail: string;
  readonly onSalaryAccount: boolean | null;
}

/** What the door takes in the engagement body, and nothing the buyer did not say. */
export interface ApportBody {
  readonly apportOrigine?: string;
  readonly apportOrigineDetail?: string;
  readonly depositOnSalaryAccount?: boolean;
}

export const emptyApportDeclaration = (): ApportDeclaration => ({
  origin: null,
  detail: "",
  onSalaryAccount: null,
});

const ORIGIN_LABELS: Readonly<Record<DepositOrigin, Parameters<typeof t>[0]>> = {
  epargne_constituee: "client.apport.origineEpargneConstituee",
  virement_ou_don_tiers: "client.apport.origineVirementOuDonTiers",
  revenu_activite: "client.apport.origineRevenuActivite",
  vente_bien: "client.apport.origineVenteBien",
  autre: "client.apport.origineAutre",
};

const LINE_ASKS: Readonly<Record<DepositOrigin, Parameters<typeof t>[0] | null>> = {
  epargne_constituee: null,
  virement_ou_don_tiers: "client.apport.consigneVirementOuDonTiers",
  revenu_activite: "client.apport.consigneRevenuActivite",
  vente_bien: "client.apport.consigneVenteBien",
  autre: "client.apport.consigneAutre",
};

/** The sentence to show for the declaration as it stands, or `null` when nothing is refused. */
export const apportRefusal = (value: ApportDeclaration): string | null =>
  value.detail.trim() !== "" && namesAForbiddenDepositWay(value.detail)
    ? t("client.apport.refusCompteFormel")
    : null;

/**
 * The body fragment for the door: `{}` when there is no deposit, `null` while the declaration is incomplete or
 * refused (the opening step stays shut), else the three fields.
 */
export function apportDeclarationBody(depositPct: number, value: ApportDeclaration): ApportBody | null {
  if (depositPct <= 0) return {};
  const origin = value.origin;
  if (!isDepositOrigin(origin) || value.onSalaryAccount === null) return null;
  const line = value.detail.trim();
  if (apportRefusal(value) !== null) return null;
  if (originNeedsALine(origin) && line === "") return null;
  if (line.length > DEPOSIT_DETAIL_MAX_LENGTH) return null;
  return {
    apportOrigine: origin,
    ...(line === "" ? {} : { apportOrigineDetail: line }),
    depositOnSalaryAccount: value.onSalaryAccount,
  };
}

export function ApportOrigine(props: {
  readonly depositPct: number;
  readonly value: ApportDeclaration;
  readonly onChange: (next: ApportDeclaration) => void;
}): ReactElement | null {
  if (props.depositPct <= 0) return null;
  const { value } = props;
  const origin = isDepositOrigin(value.origin) ? value.origin : null;
  const refusal = apportRefusal(value);
  const ask = origin === null ? null : LINE_ASKS[origin];
  return (
    <section
      data-testid="apport-origine"
      className="rounded-2xl border border-gray-200 bg-white px-4 py-4 text-sm text-gray-700"
    >
      <h3 className="mb-2 text-sm font-semibold text-gray-900">{t("client.apport.titre")}</h3>
      <fieldset className="space-y-1.5">
        <legend className="mb-1 text-gray-600">{t("client.apport.question")}</legend>
        {DEPOSIT_ORIGINS.map((one) => (
          <label key={one} className="flex items-center gap-2">
            <input
              type="radio"
              name="apport-origine"
              value={one}
              data-testid={`apport-origine-option-${one}`}
              checked={value.origin === one}
              onChange={() => {
                props.onChange({ ...value, origin: one });
              }}
            />
            {t(ORIGIN_LABELS[one])}
          </label>
        ))}
      </fieldset>
      {ask === null ? null : (
        <div className="mt-3">
          <label className="block text-gray-600" htmlFor="apport-origine-detail">
            {t("client.apport.precisionLibelle")}
          </label>
          <p className="text-xs text-gray-500" data-testid="apport-origine-detail-consigne">
            {t(ask)}
          </p>
          <input
            id="apport-origine-detail"
            data-testid="apport-origine-detail"
            type="text"
            maxLength={DEPOSIT_DETAIL_MAX_LENGTH}
            value={value.detail}
            onChange={(event) => {
              props.onChange({ ...value, detail: event.target.value });
            }}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2"
          />
        </div>
      )}
      {refusal === null ? null : (
        <p role="alert" data-testid="apport-refus" className="mt-2 text-sm text-rose-600">
          {refusal}
        </p>
      )}
      <fieldset className="mt-4 space-y-1.5">
        <legend className="mb-1 text-gray-600">{t("client.apport.compteSalaireQuestion")}</legend>
        {(
          [
            ["oui", true, "client.apport.oui"],
            ["non", false, "client.apport.non"],
          ] as const
        ).map(([id, answer, label]) => (
          <label key={id} className="flex items-center gap-2">
            <input
              type="radio"
              name="apport-compte-salaire"
              value={id}
              data-testid={`apport-compte-salaire-${id}`}
              checked={value.onSalaryAccount === answer}
              onChange={() => {
                props.onChange({ ...value, onSalaryAccount: answer });
              }}
            />
            {t(label)}
          </label>
        ))}
      </fieldset>
      {value.onSalaryAccount === false ? (
        <p className="mt-2 text-xs text-gray-600" data-testid="apport-releve-a-deposer">
          {t("client.apport.releveADeposer")}
        </p>
      ) : null}
    </section>
  );
}
